import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, RefreshCw } from "lucide-react";
import { CalendarDays as FilterDateIcon, UserCircle2 as FilterOwnerIcon } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { InlineCell } from "@/components/inline/InlineCell";
import { Button } from "@/components/ui/button";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilterBar } from "@/components/filters/FilterBar";
import { ListEmpty, PageHeader, Person, ViewsPanel } from "@/components/patterns";
import { OwnerCell } from "@/components/inline/OwnerCell";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { useRowSelection } from "@/lib/useRowSelection";
import { BulkBar, BulkButton } from "@/components/patterns";

import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import { dateLocale } from "@/lib/dateTime";
import { SampleStatusBadge, SAMPLE_STATUS_LABELS } from "./SampleStatusBadge";
import type { SampleRecord, SampleStatus } from "./sampleTypes";

const FILTER_DEFAULTS = { owner: "all", date: "any" };
const STATUSES = Object.keys(SAMPLE_STATUS_LABELS) as SampleStatus[];

interface Stats { total: number; sent: number; waiting: number; overdue: number; approved: number; rejected: number; approval_rate: number | null; avg_days_to_answer: number | null; led_to_win: number; win_rate_after_sample: number | null }

const formatDate = (d?: string | null) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-");
const target = (s: SampleRecord) => s.deal?.name || [[s.lead?.first_name, s.lead?.last_name].filter(Boolean).join(" "), s.lead?.company_name].filter(Boolean).join(" · ") || "-";

const SAMPLE_BULK_FIELDS: BulkField[] = [{ field: "feedback_due", label: "Feedback due", kind: "date", nullable: true }];

export function SamplesPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canEditInline = hasPermission("sample.update");
  const filters = usePersistentFilters("samples", FILTER_DEFAULTS);

  // "+ Add filter" on any field the API offers
  const fieldFilters = useFieldFilters("samples");
  const sorting = useSort("samples");
  const [fieldDefs, setFieldDefs] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Sample/FilterFields").then((res) => setFieldDefs(unwrapArray<FilterFieldDef>(res))).catch(() => setFieldDefs([]));
  }, []);
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<{ total: number; owners: OwnerFacet[]; statuses: { value: string; count: number }[] }>({ total: 0, owners: [], statuses: [] });
  const [stats, setStats] = useState<Stats | null>(null);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const list = usePagedList<SampleRecord>({
    deps: [tab, search, filters.values, fieldFilters.values, fieldDefs, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = filters.values;
      const query = toQuery({ status: STATUSES.includes(tab as SampleStatus) ? tab : undefined, search: search.trim(), owner: tab === "MINE" ? "me" : f.owner, ...dateRangeParams(f.date, "date"), f: fieldFilters.param(fieldDefs), sort: sorting.sort, page, page_size: 30 });
      const [res, opt] = await Promise.all([api.get(`/api/Sample/List${query}`), api.get("/api/Sample/FilterOptions").catch(() => null)]);
      if (opt) setOptions(unwrapObject(opt));
      return unwrapPage<SampleRecord>(res);
    },
  });
  useEffect(() => {
    api.get("/api/Sample/Stats").then((r) => setStats(unwrapObject(r))).catch(() => setStats(null));
  }, [list.total]);

  const count = (s: string) => options.statuses.find((x) => x.value === s)?.count ?? 0;
  const rows = list.rows;
  const sel = useRowSelection(rows.map((r) => r.id));
  const title = tab === "ALL" ? "All samples" : tab === "MINE" ? "My samples" : SAMPLE_STATUS_LABELS[tab as SampleStatus] || "Samples";

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Samples"
        subtitle="What went out to prospects"
        active={tab}
        onSelect={setTab}
        groups={[
          { items: [{ key: "ALL", label: "All samples", count: options.total }, { key: "MINE", label: "My samples" }] },
          { heading: "Status", items: STATUSES.map((s) => ({ key: s, label: SAMPLE_STATUS_LABELS[s], count: count(s), tone: s === "SENT" && (stats?.overdue ?? 0) > 0 ? ("warning" as const) : undefined })) },
        ]}
        footer={
          stats && stats.total > 0 && (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5 text-[13px]">
              {stats.overdue > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Feedback overdue</span><span className="font-mono font-semibold text-warning">{stats.overdue}</span></div>}
              {stats.approval_rate !== null && <div className="flex justify-between"><span className="text-muted-foreground">Approved</span><span className="font-mono font-semibold">{stats.approval_rate}%</span></div>}
              {stats.avg_days_to_answer !== null && <div className="flex justify-between"><span className="text-muted-foreground">Days to answer</span><span className="font-mono font-semibold">{stats.avg_days_to_answer}</span></div>}
              {stats.win_rate_after_sample !== null && <div className="flex justify-between"><span className="text-muted-foreground">Ended in a win</span><span className="font-mono font-semibold">{stats.win_rate_after_sample}%</span></div>}
            </div>
          )
        }
      />

      <PageHeader
        title={title}
        summary={<>{list.total}{(stats?.overdue ?? 0) > 0 && <span className="text-warning"> · {stats!.overdue} overdue</span>}</>}
        actions={
          <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={list.reload} disabled={list.loading}>
            <RefreshCw className={`size-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search by number, customer, deal or tracking..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(options.owners, options.total, user?.Id), defaultValue: "all" },
          { name: "date", label: "Requested", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={filters.values}
        onChange={(name, value) => filters.set(name as keyof typeof FILTER_DEFAULTS, value)}
        onReset={filters.reset}
        quickKey="samples"
        fieldFilters={{ fields: fieldDefs || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Sample/FilterValues" }}
        sort={{ fields: fieldDefs || [], value: sorting.sort, onChange: sorting.setSort }}
        shown={list.total}
        total={list.total}
        noun="samples"
      />

      <BulkBar count={sel.count} onClear={sel.clear}>
        {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
      </BulkBar>
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Sample" ids={sel.ids} fields={SAMPLE_BULK_FIELDS} onDone={() => { sel.clear(); list.reload(); }} />

      {list.loading && rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" aria-label="Select all" checked={sel.allOn} onChange={sel.toggleAll} className="rounded border-border size-4 accent-primary cursor-pointer" />
                </TableHead>
                <TableHead>Request</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Feedback due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/samples/${s.id}`)}>
                  <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label="Select row" checked={sel.selected.has(s.id)} onChange={() => sel.toggle(s.id)} className="rounded border-border size-4 accent-primary cursor-pointer" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><FlaskConical className="size-4" /></div>
                      <div>
                        <div className="font-semibold text-sm font-mono">{s.request_number}</div>
                        <div className="text-[11px] text-muted-foreground">{s.delivery_mode === "DIGITAL" ? "Trial / demo · " : ""}{s.tracking_number ? `${s.courier || "Courier"} · ${s.tracking_number}` : formatDate(s.requested_on)}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{target(s)}</div>
                    <div className="text-xs text-muted-foreground">{s.account?.name || (s.lead_id ? "Lead" : "")}</div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}><OwnerCell list="Sample" id={s.id} ownerId={s.owner_id} owner={s.owner} enabled={canEditInline && ["REQUESTED", "SENT"].includes(s.sample_status)} onSaved={(row) => list.patchRow(s.id, row)} onConflict={list.reload} /></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{s.sent_at ? formatDate(s.sent_at) : "-"}</TableCell>
                  <TableCell className={`text-sm whitespace-nowrap ${s.overdue ? "text-destructive font-medium" : ""}`}><span onClick={(e) => e.stopPropagation()}>
                      <InlineCell list="Sample" id={s.id} field="feedback_due" kind="date" value={s.feedback_due} display={formatDate(s.feedback_due)} enabled={canEditInline && ["REQUESTED", "SENT"].includes(s.sample_status)} onSaved={(row) => list.patchRow(s.id, row)} onConflict={list.reload} />
                    </span>
                  </TableCell>
                  <TableCell><SampleStatusBadge status={s.sample_status} overdue={s.overdue} /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <ListEmpty
                      noun="samples"
                      error={list.error}
                      onRetry={list.reload}
                      icon={FlaskConical}
                      filtered={!!search.trim() || tab !== "ALL" || filters.activeCount > 0 || fieldFilters.activeCount > 0}
                      onClear={() => { setSearch(""); setTab("ALL"); filters.reset(); fieldFilters.reset(); }}
                      firstUse="Samples are requested from a deal or a lead: open it and choose Request sample."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
    </div>
  );
}
